import React, { useState, useRef, useEffect } from 'react';
import { Modal, Button, message, QRCode } from 'antd';
import { ArrowRightOutlined, CheckCircleFilled, AlipayCircleFilled, WechatFilled } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import httpClient from '../utils/HttpClient';
import { SUBSCRIPTIONS_PAYMENT_URL, COURSE_PAYMENT_URL, PROFILE_INFO_URL, COURSE_PRO_PURCHASES_URL, SUBSCRIPTIONS_PLAN_URL } from '../constants/Url';
import { useDispatch, useSelector } from 'react-redux';
import { loginSuccessAction } from '../modules/auth/actions';
import { storageKeys } from '../constants/storage';
import { BsCheckCircleFill, BsCircle, BsInfo } from "react-icons/bs";

const VIPModal = ({ open, onCancel, onUpgrade, courseId, coursePrice, fetchCourse }) => {
  const { t } = useTranslation();
  const [plans, setPlans] = useState(null);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('alipay');
  const [applyLoading, setApplyLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);

  const user = useSelector((state) => state.authReducer);
  let token = user && user?.token ? user?.token : null;
  const dispatch = useDispatch();

  const runTimer = useRef(null);
  const timer = useRef(null);
  const [paymentQrcode, setPaymentQrcode] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [checkPaymentStatusParam, setCheckPaymentStatusParam] = useState(null);
  const [showCloseModal, setShowCloseModal] = useState(false);

  const handleClose = () => {
    setIsPaymentModalOpen(false);
    setPaymentQrcode(null);
    setErrorMessage(null);
    setCheckPaymentStatusParam(null);
    runTimer.current = false;
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    setCouponCode("");
    setCouponDiscount(0);
    setPaymentMethod("alipay");
  }

  const fetchPlans = async () => {
    try {
      const res = await httpClient.get(SUBSCRIPTIONS_PLAN_URL)
        .then(r => r.data)
        .catch(e => ({ status: 500, message: e?.message }));

      if (res && res.status === 200 && res.data) {
        setPlans(res.data);
      } else {
        setPlans(null);
      }
    } catch (error) {
      console.error('fetch plans error', error);
      setPlans(null);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      setPaymentQrcode(null);
      setErrorMessage(null);
      runTimer.current = false;
      if (timer.current) {
        clearTimeout(timer.current);
        timer.current = null;
      }
    }
  }, []);

  const checkPaymentStatus = async (purchaseId, method, payUrl) => {
    if (!runTimer.current) return;
    setErrorMessage(null);
    try {
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = null;
      const res = await httpClient.post(`/courses/payment-query`, {
        method: method,
        purchaseId: purchaseId
      });
      if (res.data && res.data.status === 200) {
        const order = res.data.data || res.data;
        if (order.paymentStatus === "paid") {
          runTimer.current = false;
          const successMsg = t('text_purchase_success');
          message.success(successMsg);
          if (fetchCourse) fetchCourse();
          setIsPaymentModalOpen(false);
          await fetchProfiles();
          if (onCancel) onCancel();
          return;
        }
      } else {
        runTimer.current = false;
        setErrorMessage(t('text_failed_loading_order_detail'));
        return;
      }
      timer.current = setTimeout(() => {
        checkPaymentStatus(purchaseId, method, payUrl);
      }, 3000);
    } catch (err) {
      runTimer.current = false;
      console.error('Failed to check order status:', err);
      setErrorMessage(err.message || 'Error checking order status');
    }
  };

  const saveAuth = (token, user) => {
    dispatch(loginSuccessAction({ token, user: user, photo: user?.picUrl }));
    localStorage.setItem(storageKeys.TOKEN, btoa(token));
    localStorage.setItem(storageKeys.USER, JSON.stringify(user));
  };

  const fetchProfiles = async () => {
    try {
      const res = await httpClient.get(PROFILE_INFO_URL);
      const member = res?.data?.member;
      const status = res?.data?.status;

      if (status === 200) {
        saveAuth(token, member);
      }
    } catch (error) {
      console.error("fetchData -> error", error);
    }
  };

  const handleOpenPaymentModal = () => {
    setIsPaymentModalOpen(true);
  };

  const handleClearCoupon = () => {
    setCouponDiscount(0);
    setCouponCode('');
    message.success(t('text_coupon_cancelled'));
  };

  //Apply Coupon
  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) {
      message.warning(t('text_input_coupon'));
      return;
    }
    setApplyingCoupon(true);
    try {
      const res = await httpClient.post('/coupons-scan-validate', {
        couponCode: couponCode.trim(),
        currentOrderTotal: coursePrice ? coursePrice : 0
      });
      if (res?.data?.status === 200) {
        const data = res?.data?.data || res?.data;
        const discount = data?.discountAmount || data?.discount || 0;
        message.success(t("text_coupon_success"));
        setCouponDiscount(discount);
      }else{
        message.error(res?.data?.message);
        setCouponCode("")
      }
    } catch (err) {
      message.error(t(err?.response?.data?.message || err?.message || 'text_coupon_failed'));
      setCouponCode("")
    } finally {
      setApplyingCoupon(false);
    }
  };

  const handleConfirmPayment = async () => {
    if (!courseId) {
      message.error(t('text_no_courseid'));
      return;
    }

    try {
      setApplyLoading(true);
      // 1. Create Purchase Order
      const orderResponse = await httpClient.post(COURSE_PRO_PURCHASES_URL, {
        courseId: courseId,
        method: paymentMethod,
        currency: "CNY",
        couponCode: couponDiscount > 0 ? couponCode : undefined
      });
      
      const orderRes = orderResponse?.data;
      
      if (orderRes && (orderRes.status === 200 || orderRes.status === 201 || String(orderRes.status) === '200' || orderRes.code === 200 || orderRes.code === 0)) {
        
        const transactionData = orderRes.data || orderRes;
        const payUrl = transactionData?.paymentUrl || transactionData?.payUrl || transactionData?.redirectUrl || transactionData?.url || orderRes?.paymentUrl || orderRes?.payUrl;

        // If backend returns an external payment gateway URL (Alipay/WeChat), redirect user to pay
        if (payUrl) {
          window.location.href = payUrl;
          return;
        }

        var code_url = null;
        if (paymentMethod === 'alipay') {
          code_url = transactionData?.orderinfo?.qrCode;
        } else if (paymentMethod === 'wechatpay') {
          code_url = transactionData?.orderinfo?.code_url;
        }
        setPaymentQrcode(code_url);

        if (code_url) {
          runTimer.current = true;
          setCheckPaymentStatusParam({
            purchaseId: transactionData.purchaseId,
            method: paymentMethod,
            payUrl: payUrl
          });
          checkPaymentStatus(transactionData.purchaseId, paymentMethod, payUrl);
        }

      } else {
        const errorMsg = typeof orderRes?.message === 'string' ? t(orderRes.message) : (t(orderRes?.msg) || t('text_create_order_failed'));
        message.error(errorMsg);
      }
    } catch (error) {
      console.error('payment error', error);
      const errorData = error?.response?.data || error;
      const errorMsg = typeof errorData?.message === 'string' ? t(errorData.message) : (error?.message || t('text_operation_failed'));
      message.error(errorMsg);
    } finally {
      setApplyLoading(false);
    }
  };

  const isSuperEnabled = Boolean(plans?.super && !plans?.super?.disabled && plans?.super?.status !== false);

  return (
    <>
      <Modal
        open={open}
        onCancel={onCancel}
        footer={null}
        width={400}
        centered
        className="vip-purchase-modal"
        styles={{ body: { padding: '40px 24px 30px 24px' } }}
      >
        <div className="flex flex-col items-center justify-center">
          {/* Exclamation Icon */}
          <div className="w-16 h-16 bg-[#fce4ec] rounded-full flex items-center justify-center mb-6">
            <span className="text-[#e91e63] text-4xl font-bold leading-none -mt-1">!</span>
          </div>

          {/* Title */}
          <h2 className="text-lg sm:text-xl font-bold text-gray-800 mb-8 tracking-wide">
            {t('text_confirm_buy_course')}
          </h2>

          {/* Confirm Button */}
          <Button 
            type="primary" 
            onClick={handleOpenPaymentModal}
            className="w-full max-w-[280px] bg-[#cc2e55] hover:bg-[#b3274a] border-none h-[44px] rounded-lg text-base font-medium tracking-widest mb-6"
          >
            {t('text_confirm')}
          </Button>

          {/* Upgrade Link */}
          {isSuperEnabled && (
            <div 
              onClick={onUpgrade}
              className="w-full max-w-[280px] text-[#cc2e55] h-[44px] flex items-center justify-center gap-1 cursor-pointer transition-colors text-[15px] font-medium"
            >
              {t('text_activate_svip_prompt')} <ArrowRightOutlined className="text-sm font-bold" />
            </div>
          )}
        </div>
        
        <style jsx global>{`
          .vip-purchase-modal .ant-modal-content {
            border-radius: 16px;
            overflow: hidden;
          }
        `}</style>
      </Modal>

      <Modal
        open={isPaymentModalOpen}
        onCancel={() => {
          if (paymentQrcode) {
            setShowCloseModal(true);
          } else {
            handleClose();
          }
        }}
        footer={null}
        title={null}
        closable={true}
        width={450}
        centered
        styles={{ body: { padding: '24px', borderRadius: '12px' } }}
      >
        {paymentQrcode ? (
          <>
          <div className="flex flex-col items-center justify-center py-2">
            <div className="text-lg font-bold text-gray-800 text-center mb-4">{t('text_tobepaid_qrcode')}</div>
            <QRCode
              value={paymentQrcode}
              size={220}
              icon={paymentMethod === 'wechatpay' ? "https://ts4.tc.mm.bing.net/th/id/ODF.BvtHqZTl6qLypPDIASUGoA?w=32&h=32&qlt=97&pcl=fffffa&o=6&pid=1.2" : "https://ts1.tc.mm.bing.net/th/id/ODF.tFQwCWKfvMeJYOFxHknauw?w=32&h=32&qlt=90&pcl=fffffa&o=6&pid=1.2"}
              iconSize={{
                width: 32,
                height: 32,
              }}
            />
          </div>
          <div style={{marginTop: 10, marginBottom: errorMessage ? 10 : 0, display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5}}>
            <div className="bg-[#fff0f4] p-0.5 rounded-full">
              <BsInfo className="text-[#e80d44] text-xl shrink-0" />
            </div>
            <span className="text-[#e80d44] text-sm font-medium">{t("text_qrcode_desc")}</span>
          </div>
          {errorMessage ? <div className="flex flex-col items-center justify-center w-full">
            <span style={{color: 'red'}} className="text-sm text-gray-500 font-medium">{errorMessage}</span>

            <button
              onClick={() => {
                checkPaymentStatus(checkPaymentStatusParam.orderId, checkPaymentStatusParam.method, checkPaymentStatusParam.payUrl);
              }}
              style={{width: '100%'}}
              className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 hover:scale-105 active:scale-95`}
            >
              {t("text_retry")}
            </button>
          </div> : null}
          </>
        ) : (
          <div className="flex flex-col gap-4 mt-2">
            <div className="text-lg font-bold text-gray-800 text-center mb-2">{t('text_select_payment')}</div>
            
            <div 
              className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'alipay' ? 'border-[#e91e63] bg-[#fdf2f5]' : 'border-gray-100 hover:border-gray-200'}`}
              onClick={() => setPaymentMethod('alipay')}
            >
              <div className="flex items-center gap-3">
                <AlipayCircleFilled className="text-[#1677ff] text-[28px]" />
                <span className="font-semibold text-gray-800 text-base">{t('text_alipay_payment')}</span>
              </div>
              {paymentMethod === 'alipay' ? <CheckCircleFilled className="text-[#52c41a] text-xl" /> : <div className="w-[20px] h-[20px] rounded-full border-2 border-gray-300"></div>}
            </div>

            <div 
              className={`flex items-center justify-between p-4 rounded-xl border-2 cursor-pointer transition-all ${paymentMethod === 'wechatpay' ? 'border-[#e91e63] bg-[#fdf2f5]' : 'border-gray-100 hover:border-gray-200'}`}
              onClick={() => setPaymentMethod('wechatpay')}
            >
              <div className="flex items-center gap-3">
                <WechatFilled className="text-[#09b83e] text-[28px]" /> 
                <span className="font-semibold text-gray-800 text-base">{t('text_wechat_payment')}</span>
              </div>
              {paymentMethod === 'wechatpay' ? <CheckCircleFilled className="text-[#52c41a] text-xl" /> : <div className="w-[20px] h-[20px] rounded-full border-2 border-gray-300"></div>}
            </div>

            {/* Coupon Section hidden for education video purchase */}

            <div className="space-y-3 bg-[#fcfcfc] border border-gray-100 rounded-xl p-4 mt-2 shadow-sm">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-500">{t("text_product_total")}</span>
                <span className="text-gray-900 font-medium">¥{Number(coursePrice || 0).toFixed(2)}</span>
              </div>
              
              {couponDiscount > 0 && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-500">{t("text_coupon_deduct")}</span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2 bg-[#f7f8fc] border border-dashed border-[#d4d8ef] text-[#5e6db0] px-2.5 py-1 rounded-md shadow-sm" style={{marginRight:"2rem"}}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="1.1em" height="1.1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2Z"/>
                        <path d="M9 5v14"/>
                      </svg>
                      <span className="text-xs font-bold tracking-wide">{couponCode}</span>
                      <button 
                        onClick={handleClearCoupon}
                        className="hover:text-[#e80d44] transition-colors ml-1 cursor-pointer flex items-center justify-center"
                        title={t("text_cancel_coupon")}
                      >
                        <svg stroke="currentColor" fill="none" strokeWidth="2" viewBox="0 0 24 24" strokeLinecap="round" strokeLinejoin="round" height="1.1em" width="1.1em"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                      </button>
                    </div>
                    <span className="text-[#e80d44] font-medium">-¥{couponDiscount?.toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t border-gray-100 flex justify-between items-center">
                <span className="text-gray-600 font-medium">{t("text_tobepaid_amount")}</span>
                <span className="text-[#e80d44] text-2xl font-bold tracking-tight">¥{Number(coursePrice || 0).toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Button 
                type="primary" 
                loading={applyLoading}
                className="w-full bg-[#e91e63] hover:bg-[#d81557] border-none h-[44px] rounded-lg text-base font-bold mt-4"
                onClick={handleConfirmPayment}
              >
                {t('text_confirm_payment')}
              </Button>
            </div>
          </div>
        )}
        <Modal
          open={showCloseModal}
          onCancel={() => {
            setShowCloseModal(false);
          }}
          closeable={true}
          footer={null}
          centered
          width={400}
          title={null}
          styles={{
            body: {
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '24px 16px 32px 16px',
            }
          }}
        >
          <div className="flex flex-col items-center justify-center p-2">
            <div style={{padding: 12}} className="bg-[#fff0f4] rounded-full">
              <BsInfo size={36} color="#e80d44" />
            </div>
            <span className="text-sm shrink-0 font-medium mt-4">{t("text_qrcode_warning")}</span>
            <button
              onClick={() => {
                setShowCloseModal(false);
                setTimeout(() => {
                  handleClose();
                }, 100);
              }}
              style={{width: '100%'}}
              className={`rounded-xl bg-gradient-to-r mt-4 from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 font-semibold text-sm transition-all duration-300 flex justify-center items-center gap-2 hover:scale-105 active:scale-95`}
            >
              {t("text_qrcode_confirm")}
            </button>
          </div>
        </Modal>
      </Modal>
    </>
  );
};

export default VIPModal;
